import { Fetcher, dig } from '@this/src/util';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@this/shared/ui/data_displays/table';
import { Select } from '@this/shared/ui/inputs/select';

interface PrefectureRailwayAreaResponse {
  id: number;
  area: string;
  service_id: string;
  ticketing_qr_service: number;
}

interface PrefectureResponse {
  id: number;
  region_id: number;
  name: string;
  created_at: string;
  updated_at: string;
  railway_area: PrefectureRailwayAreaResponse | null;
  selected_railway_area_id: number | null;
}

interface RailwayAreaResponse {
  id: number;
  area: string;
  service_id: string;
  ticketing_qr_service: number;
  created_at: string;
  updated_at: string;
}

const Prefectures = () => {
  const [prefecturesLoading, setPrefecturesLoading] = useState(false);
  const [prefectures, setPrefectures] = useState<PrefectureResponse[]>([]);
  const [allRailwayAreas, setAllRailwayAreas] = useState<RailwayAreaResponse[]>([]);
  const [selectedRailwayAreas, setSelectedRailwayAreas] = useState<Record<number, number | null>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchPrefectures = () => {
    setPrefecturesLoading(true);
    Fetcher.get<PrefectureResponse[]>('/admin/prefectures.json')
      .then(res => {
        setPrefectures(res);
        // 初期選択された鉄道エリアを設定
        const initialSelections: Record<number, number | null> = {};
        res.forEach(prefecture => {
          initialSelections[prefecture.id] = prefecture.selected_railway_area_id;
        });
        setSelectedRailwayAreas(initialSelections);
      })
      .catch(e => {
        setErrorMessage(
          dig(e, 'responseJSON', 'message') || '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        );
      })
      .finally(() => {
        setPrefecturesLoading(false);
      });
  };

  const fetchRailwayAreas = () => {
    Fetcher.get<RailwayAreaResponse[]>('/admin/railway_areas.json')
      .then(res => {
        setAllRailwayAreas(res);
      })
      .catch(e => {
        console.error('Failed to fetch railway areas:', e);
      });
  };

  const savePrefectureRailwayArea = (prefectureId: number, railwayAreaId: number | null) => {
    Fetcher.put(`/admin/prefectures/${prefectureId}.json`, {
      railway_area_id: railwayAreaId
    })
      .then(() => {
        setSuccessMessage('保存しました。');
        setErrorMessage(null);
      })
      .catch(e => {
        setErrorMessage(
          dig(e, 'responseJSON', 'message') || '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        );
        setSuccessMessage(null);
      });
  };

  useEffect(() => {
    fetchPrefectures();
    fetchRailwayAreas();
  }, []);

  return (
    <>
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '16px' }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} style={{ marginBottom: '16px' }}>
          {successMessage}
        </Alert>
      )}
      {prefecturesLoading ? (
        <Loading />
      ) : (
        <TableContainer>
          <Table style={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>地域ID</TableCell>
                <TableCell>都道府県名</TableCell>
                <TableCell>鉄道エリア</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prefectures.map(prefecture => (
                <TableRow key={prefecture.id}>
                  <TableCell>{prefecture.id}</TableCell>
                  <TableCell>{prefecture.region_id}</TableCell>
                  <TableCell>{prefecture.name}</TableCell>
                  <TableCell>
                    <Select
                      value={selectedRailwayAreas[prefecture.id]?.toString() || ''}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const railwayAreaId = parseInt(e.target.value, 10) || null;
                        setSelectedRailwayAreas(prev => ({
                          ...prev,
                          [prefecture.id]: railwayAreaId
                        }));
                        savePrefectureRailwayArea(prefecture.id, railwayAreaId);
                      }}
                    >
                      <option value="">選択してください</option>
                      {allRailwayAreas.map(railwayArea => (
                        <option key={railwayArea.id} value={railwayArea.id.toString()}>
                          {railwayArea.area}
                        </option>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default observer(Prefectures);
